import React, { useEffect, useState } from 'react';
import { Auth0Provider as Auth0ProviderBase } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { clearAuthReturnTo, getAuthReturnTo, setAuthReturnTo } from '../utils/authRedirect.js';
import { isNative, isTavernApp } from '../utils/platform.js';
import { API_CONFIG } from '../config/api.js';

export function Auth0Provider({ children }) {
  const navigate = useNavigate();
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  // For native apps, fetch Auth0 config from the backend at runtime
  // so we don't need to bake env vars into the build.
  useEffect(() => {
    if (!isNative()) return;
    const backendUrl = API_CONFIG.BACKEND_URL;
    const configUrl = `${backendUrl}/api/config`;
    console.log('[Auth0Provider] Fetching config from:', configUrl);
    fetch(configUrl)
      .then(res => {
        if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log('[Auth0Provider] Config loaded successfully');
        setRuntimeConfig(data);
      })
      .catch(err => {
        console.error('[Auth0Provider] Config fetch failed:', err?.message || err);
        setConfigError(err?.message || 'Network error');
      });
  }, []);

  // Use runtime config for native, env vars for web
  const domain = isNative() ? runtimeConfig?.auth0_domain : import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = isNative() ? runtimeConfig?.auth0_native_client_id : import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = isNative() ? runtimeConfig?.auth0_audience : import.meta.env.VITE_AUTH0_AUDIENCE;
  const redirectUri = isNative()
    ? `com.fabletable.tavern://${domain}/capacitor/com.fabletable.tavern/callback`
    : import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin;

  const onRedirectCallback = (appState) => {
    const storedReturnTo = getAuthReturnTo();
    const returnTo = appState?.returnTo || storedReturnTo || '/';

    if (window.location.pathname === '/callback') {
      setAuthReturnTo(returnTo);
      return;
    }

    clearAuthReturnTo();

    // In Capacitor native or tavern-app mode, always redirect to /tavern-app
    if (isTavernApp() || returnTo?.startsWith('/tavern-app')) {
      navigate('/tavern-app', { replace: true });
      return;
    }

    navigate(returnTo);
  };

  // Native: wait for runtime config to load
  if (isNative() && !runtimeConfig) {
    if (configError) {
      return (
        <div style={{ padding: 20, color: '#d97706', fontFamily: 'serif', textAlign: 'center' }}>
          <p>Unable to connect to server</p>
          <p style={{ fontSize: 12, color: '#78716c', marginTop: 8 }}>{configError}</p>
        </div>
      );
    }
    return null; // Still loading config — splash screen is still showing
  }

  if (!domain || !clientId) {
    console.error('Auth0 configuration missing. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID');
    return <div>Auth0 configuration error - check environment variables</div>;
  }

  return (
    <Auth0ProviderBase
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: "openid profile email offline_access"
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={false}
    >
      {children}
    </Auth0ProviderBase>
  );
}
